import React, { useEffect, useLayoutEffect, useState, useCallback } from 'react';
import './OnboardingTour.css';

// Contextual spotlight tour — highlights a real element on the page (via
// getElementById) rather than a slideshow of screenshots. `steps`:
// [{ targetId, title, body }]. The spotlight is a single fixed-position box
// whose oversized box-shadow dims everything else on the page (a standard,
// dependency-free CSS technique) — no overlay library needed.
const OnboardingTour = ({ steps, onFinish }) => {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[index];

  const measure = useCallback(() => {
    const el = document.getElementById(step.targetId);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step.targetId]);

  useLayoutEffect(() => {
    const el = document.getElementById(step.targetId);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const t = setTimeout(measure, 260); // wait for scroll to settle
    return () => clearTimeout(t);
  }, [step.targetId, measure]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [measure]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onFinish();
      if (e.key === 'Enter' || e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const next = () => {
    if (index < steps.length - 1) setIndex(index + 1);
    else onFinish();
  };
  const skip = () => onFinish();

  // Tooltip placement: below the target if there's room, otherwise above.
  const tooltipStyle = {};
  const pad = 14;
  if (rect) {
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    const placeBelow = spaceBelow > 180 || rect.top < 180;
    tooltipStyle.left = Math.min(Math.max(rect.left, 16), window.innerWidth - 320 - 16);
    tooltipStyle.top = placeBelow ? rect.top + rect.height + pad : Math.max(16, rect.top - pad - 160);
  }

  return (
    <div className="ob-root" role="dialog" aria-modal="true" aria-label="Product tour">
      {rect ? (
        <div
          className="ob-spotlight"
          style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 }}
        />
      ) : (
        <div className="ob-spotlight ob-spotlight--fallback" />
      )}

      <div className="ob-tooltip" style={rect ? tooltipStyle : undefined}>
        <div className="ob-tooltip-dots">
          {steps.map((_, i) => <span key={i} className={`ob-dot ${i === index ? 'active' : ''}`} />)}
        </div>
        <h4 className="ob-tooltip-title">{step.title}</h4>
        <p className="ob-tooltip-body">{step.body}</p>
        <div className="ob-tooltip-actions">
          <button type="button" className="ob-btn-skip" onClick={skip}>Skip</button>
          <button type="button" className="ob-btn-next" onClick={next}>
            {index < steps.length - 1 ? 'Next' : 'Got it'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
